import {SpecialistDecision} from 'app/departures/departure/tech-specialist-view/specialist-decision';
import {ProvisionalAgreement} from './provisional-agreement';

export interface DepartureProperties {
  id?: string; // set by the backend on persist
  departureId: string;
  createdBy?: string; // set by the backend on persist
  createdAt?: number; // set by the backend on persist
  approximateCreatedAt?: boolean;
  properties: Record<string, string | number | boolean>;
}

export enum DepartureProperty {
  APPRAISED = 'APPRAISED',
  DAS_2_DIARY_STATUS = 'DAS_2_DIARY_STATUS',
  DAS_2_DIARY_WARNING = 'DAS_2_DIARY_WARNING',
  NUMBER_OF_CONDITIONS = 'NUMBER_OF_CONDITIONS',
  PM_APPROVE_PRE_DETERMINATION = 'PM_APPROVE_PRE_DETERMINATION',
  PM_APPROVE_WITH_CONDITIONS_PRE_DETERMINATION = 'PM_APPROVE_WITH_CONDITIONS_PRE_DETERMINATION',
  PM_LIAISED = 'PM_LIAISED',
  PM_REJECT_PRE_DETERMINATION = 'PM_REJECT_PRE_DETERMINATION',
  PROPOSER_DECLARATION = 'PROPOSER_DECLARATION',
  PROVISIONAL_AGREEMENT = 'PROVISIONAL_AGREEMENT',
  SPECIALIST_RECOMMENDATION = 'SPECIALIST_RECOMMENDATION',
  TARGET_DATE = 'TARGET_DATE',
}

export function getDeparturePropertyChanges(
  initial: DepartureProperties,
  changed: DepartureProperties
): DeparturePropertiesChangeEvent[] {
  if (!changed && !initial) {
    return [];
  } else if (!changed && initial) {
    return Object.keys(initial.properties).map((key: string) => ({
      key: key,
      prevValue: initial.properties[key],
      currValue: void 0,
      createdBy: void 0,
    }));
  } else if (changed && !initial) {
    return Object.keys(changed.properties).map((key: string) => ({
      key: key,
      prevValue: void 0,
      currValue: changed.properties[key],
      createdBy: changed.createdBy,
    }));
  } else {
    const changes: DeparturePropertiesChangeEvent[] = [];

    Object.keys(initial.properties).forEach((key: string) => {
      // add a change event for each changed property
      if (initial.properties[key] !== changed.properties[key]) {
        changes.push({
          key: key,
          prevValue: initial.properties[key],
          currValue: changed.properties[key],
          createdBy: changed.createdBy,
        });
      }
    });

    // set prevValue to undefined for any keys that do not appear in initial
    Object.keys(changed.properties).forEach((key: string) => {
      if (!initial.properties.hasOwnProperty(key)) {
        changes.push({
          key: key,
          prevValue: void 0,
          currValue: changed.properties[key],
          createdBy: changed.createdBy,
        });
      }
    });

    return changes;
  }
}

export interface DeparturePropertiesChangeEvent {
  key: string;
  prevValue: string | number | boolean;
  currValue: string | number | boolean;
  createdBy: string; // User who made the changes
}

export function createDeparturePropertiesChange(
  latest: DepartureProperties,
  newProperties: Record<string, string | number | boolean>,
  departureId: string
): DepartureProperties {
  const latestProperties = latest && latest.properties;
  return {
    departureId,
    properties: {...latestProperties, ...newProperties},
  };
}

export function hasPredetermined(properties: DepartureProperties, recommendation?: SpecialistDecision): boolean {
  if (properties && properties.properties) {
    if (!recommendation) {
      recommendation = properties.properties[DepartureProperty.SPECIALIST_RECOMMENDATION] as SpecialistDecision;
    }
    switch (recommendation) {
      case SpecialistDecision.APPROVED:
        return !!properties.properties[DepartureProperty.PM_APPROVE_PRE_DETERMINATION];
      case SpecialistDecision.APPROVED_WITH_CONDITIONS:
        return !!properties.properties[DepartureProperty.PM_APPROVE_WITH_CONDITIONS_PRE_DETERMINATION];
      case SpecialistDecision.REJECTED:
        return !!properties.properties[DepartureProperty.PM_REJECT_PRE_DETERMINATION];
    }
  }

  return false;
}

export function getProvisionalAgreementState(properties: DepartureProperties): ProvisionalAgreement {
  if (properties && properties.properties) {
    return ProvisionalAgreement[properties.properties[DepartureProperty.PROVISIONAL_AGREEMENT] as string];
  }

  return null;
}
